/* Pool Direct Supply — Pool Calculator page (/pages/pool-calculator).
   Ports the approved mockup's calculator script function-for-function.
   Sections expose inputs/outputs via data-calc-input / data-calc-output
   attributes instead of ids, so this file stays decoupled from any one
   section's markup. Loaded once, deferred, by templates/page.pool-calculator.liquid.

   Layout: helpers and every section's functions are declared first, and
   all the wiring/initial runs happen together at the bottom. volume() now
   pushes its result into the chem/equip sections on every change, so
   equip() and its constants must already exist by the time volume() first
   runs — hence one init block at the end instead of per-section init. */
(function () {
  'use strict';

  function input(name) {
    return document.querySelector('[data-calc-input="' + name + '"]');
  }

  function output(name) {
    return document.querySelector('[data-calc-output="' + name + '"]');
  }

  /* Every field on this page is a physical measurement (length, depth,
     gallons, ppm, hours, degrees…) that is never legitimately negative.
     Clamping here — rather than in each formula — closes off negative
     input as a source of negative-looking doses/BTUs/GPM in one place;
     garbage and empty values already fell back to 0 via the mockup's own
     `parseFloat(...) || 0`, this just extends that same fallback to
     negatives instead of passing them through. A *computed* delta
     (target - now) can still land negative — that's the intended signal
     the dosing formulas branch on — this only stops a single raw reading
     from being negative. */
  function num(name) {
    var el = input(name);
    var n = el ? parseFloat(el.value) : NaN;
    return (isNaN(n) || n < 0) ? 0 : n;
  }

  function setOutput(name, value) {
    var el = output(name);
    if (el) el.textContent = value;
  }

  function setInput(name, value) {
    var el = input(name);
    if (el) el.value = value;
  }

  /* Raw string value — num() coerces to a number, which breaks a <select>
     like ftype whose values are non-numeric ("cart"/"sand"/"de"). */
  function val(name) {
    var el = input(name);
    return el ? el.value : '';
  }

  function fmt(n) {
    return Math.round(n).toLocaleString('en-US');
  }

  /* Switches to gallons at >=128 fl oz. */
  function oz(n) {
    return n < 128
      ? (Math.round(n * 2) / 2).toLocaleString() + ' fl oz'
      : (Math.round(n / 128 * 10) / 10).toLocaleString() + ' gal';
  }

  /* Shows ounces under 1 lb. */
  function lbs(n) {
    return n < 1
      ? Math.round(n * 16) + ' oz'
      : (Math.round(n * 10) / 10).toLocaleString() + ' lbs';
  }

  function money(cents) {
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: currency });
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function onInput(names, handler) {
    names.forEach(function (name) {
      var el = input(name);
      if (el) {
        el.addEventListener('input', handler);
        /* <select> fires change, not always input (older Safari). */
        if (el.tagName === 'SELECT') el.addEventListener('change', handler);
      }
    });
  }

  var STORAGE_KEY = 'pdsPoolCalc';

  function saveState(state) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      /* Private browsing / storage disabled — the calculator still works, it just won't remember. */
    }
  }

  function loadState() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  /* ---------- shared pool state (owned by 01 volume) ---------- */
  var shape = 'rect';
  var ptype = 'ig';
  var gallons = 19200;
  var area = 512;
  var DIMENSION_FIELDS = ['vL', 'vW', 'vDia', 'vA', 'vB', 'vS', 'vE'];

  /* What picking a pool type does to the volume fields. Above-ground pools
     are almost always round with a flat ~4 ft (52″ wall) water depth; spas
     are small and shallow. */
  var POOL_DEFAULTS = {
    ig: { shape: 'rect', fields: { vL: 32, vW: 16, vS: 3.5, vE: 6.5 } },
    ag: { shape: 'round', fields: { vDia: 24, vS: 4, vE: 4 } },
    spa: { shape: 'rect', fields: { vL: 8, vW: 8, vS: 3, vE: 3 } }
  };

  /* Pools turn over every 6–12 hours; a spa's filtration should turn the
     whole body of water over in 30–60 minutes. */
  var TURNOVER = {
    pool: { options: ['6', '8', '10', '12'], selected: '8' },
    spa: { options: ['0.5', '1'], selected: '1' }
  };

  var POOL_LABEL = { ig: 'in-ground pool', ag: 'above-ground pool', spa: 'spa' };

  /* ---------- hero: quick-answer widget ---------- */
  /* Rectangular pool only, feet, US gallons. Typing here also writes the
     same rectangle into section 01, so the rest of the page follows. */
  function quick() {
    var g = num('qL') * num('qW') * num('qD') * 7.5;
    setOutput('qGal', fmt(g));
    setOutput('qArea', fmt(num('qL') * num('qW')));
  }

  function quickToVolume() {
    quick();
    if (!input('vL')) return;
    setShape('rect');
    setInput('vL', num('qL'));
    setInput('vW', num('qW'));
    setInput('vS', num('qD'));
    setInput('vE', num('qD'));
    volume();
  }

  /* ---------- 01 volume ---------- */
  function showFields() {
    document.querySelectorAll('[data-calc-for]').forEach(function (field) {
      var shapes = (field.getAttribute('data-calc-for') || '').split(' ');
      field.style.display = shapes.indexOf(shape) !== -1 ? '' : 'none';
    });
  }

  function setChip(value) {
    ptype = value;
    document.querySelectorAll('[data-calc-chip="ptype"]').forEach(function (chip) {
      chip.setAttribute('aria-pressed', chip.getAttribute('data-calc-value') === value ? 'true' : 'false');
    });
    setInput('ePtype', value);
    setTurnoverOptions(value);
  }

  function setShape(value) {
    shape = value;
    document.querySelectorAll('[data-calc-shape]').forEach(function (tile) {
      tile.setAttribute('aria-pressed', tile.getAttribute('data-calc-shape') === value ? 'true' : 'false');
    });
    showFields();
  }

  /* Chip click (01) and the pool-type select (03) both land here. */
  function selectPoolType(value) {
    if (!POOL_DEFAULTS[value]) return;
    setChip(value);
    var d = POOL_DEFAULTS[value];
    setShape(d.shape);
    Object.keys(d.fields).forEach(function (name) { setInput(name, d.fields[name]); });
    if (input('vL')) {
      volume();
    } else if (typeof equip === 'function') {
      equip();
    }
  }

  function volume() {
    var d = (num('vS') + num('vE')) / 2;
    var a = 0;
    var f = '';
    if (shape === 'rect') { a = num('vL') * num('vW'); f = 'L × W × avg depth × 7.5'; }
    if (shape === 'round') { a = 0.785 * num('vDia') * num('vDia'); f = 'Diameter² × 0.785 × avg depth × 7.5'; }
    if (shape === 'oval') { a = 0.785 * num('vL') * num('vW'); f = 'L × W × 0.785 × avg depth × 7.5'; }
    if (shape === 'kidney') { a = 0.45 * (num('vA') + num('vB')) * num('vL'); f = '(A + B) × L × 0.45 × avg depth × 7.5'; }
    /* Roman end, Grecian (cut corners) and freeform all take the industry
       0.85 rule of thumb — a rectangle's footprint minus the curved or
       clipped ends. Same constant, kept as three branches because each
       shape names itself in the formula the visitor is shown. */
    if (shape === 'roman') { a = 0.85 * num('vL') * num('vW'); f = 'L × W × 0.85 × avg depth × 7.5'; }
    if (shape === 'grecian') { a = 0.85 * num('vL') * num('vW'); f = 'L × W × 0.85 × avg depth × 7.5'; }
    if (shape === 'freeform') { a = 0.85 * num('vL') * num('vW'); f = 'L × W × 0.85 × avg depth × 7.5'; }
    gallons = a * d * 7.5;
    area = a;
    setOutput('rGal', fmt(gallons));
    setOutput('rArea', fmt(area));
    setOutput('rDepth', d.toFixed(1) + ' ft');
    setOutput('rLitres', fmt(gallons * 3.785));
    setOutput('rFormula', f);

    var state = { shape: shape, ptype: ptype };
    DIMENSION_FIELDS.forEach(function (name) { state[name] = num(name); });
    saveState(state);

    /* Changing the pool size updates the chemical and equipment sections
       straight away — the "use this" button is now just a scroll shortcut. */
    propagateGallons();
  }

  /* Shared by volume() and the "use this" button. */
  function propagateGallons() {
    setInput('cGal', Math.round(gallons));
    setInput('eGal', Math.round(gallons));
    setInput('eArea', Math.round(area));
    setInput('hGal', fmt(gallons));
    if (input('cGal')) chem();
    if (input('eGal')) equip();
  }

  /* ---------- 02 chemical dosing — rules of thumb per 10,000 gal ---------- */
  function chem() {
    var k = num('cGal') / 10000;

    var fc = Math.max(0, num('fcTgt') - num('fcNow'));
    setOutput('fcLiq', fc ? oz(fc * 10.7 * k) : '0');            // 10.7 fl oz 12.5% per ppm per 10k
    setOutput('fcCal', fc ? lbs(fc * 2 / 16 * k) : '0');         // 2 oz 65% cal-hypo per ppm per 10k

    var sh = num('shTgt');
    setOutput('shCal', lbs(sh * 2 / 16 * k));
    setOutput('shLiq', oz(sh * 10.7 * k));

    var ta = num('taTgt') - num('taNow');
    setOutput('taUp', ta > 0 ? lbs(ta / 10 * 1.5 * k) : '—');    // 1.5 lb baking soda per 10 ppm per 10k
    setOutput('taDown', ta < 0 ? oz(-ta / 10 * 26 * k) : '—');   // ~26 fl oz 31.45% muriatic per 10 ppm per 10k

    var cya = num('cyaTgt') - num('cyaNow');
    setOutput('cyaOut', cya > 0 ? lbs(cya / 10 * 13 / 16 * k) : '0');  // 13 oz per 10 ppm per 10k
    setOutput('cyaNote', cya < 0 ? 'Drain & refill ' + Math.round(-cya / num('cyaNow') * 100) + '%' : '—');

    var salt = num('saltTgt') - num('saltNow');
    var saltLbs = salt > 0 ? salt / 120 * k : 0;                 // 1 lb per 1,000 gal ≈ +120 ppm → per 10k: ppm/120 *10 … handled via k*10
    setOutput('saltOut', lbs(saltLbs * 10));
    setOutput('saltBags', salt > 0 ? Math.ceil(saltLbs * 10 / 40) + ' bags' : '—');

    var ch = num('chTgt') - num('chNow');
    setOutput('chOut', ch > 0 ? lbs(ch / 10 * 1.25 * k) : '0');  // 1.25 lb 77% CaCl2 per 10 ppm per 10k
    setOutput('chNote', ch < 0 ? 'Only dilution lowers it' : 'Dissolve in a bucket first');
  }

  /* ---------- 03 equipment sizing ---------- */

  /* Practical flow ceiling per plumbing size (~6 ft/s suction velocity). */
  var PIPE_CAP = { '1.5': 45, '2': 80, '2.5': 120 };
  var PIPE_LABEL = { '1.5': '1.5″', '2': '2″', '2.5': '2.5″+' };

  /* A cover stops most evaporation, which is the bulk of a pool's heat
     loss, so a covered pool can take a heat pump ~30% smaller. Kept
     conservative on purpose — undersizing is the expensive mistake. */
  var COVER_FACTOR = 0.7;

  /* Salt cell must be rated for at least 1.5× the pool. */
  var SALT_CELL_FACTOR = 1.5;

  var FUEL_LABEL = { ng: 'natural gas', lp: 'propane' };

  function setTurnoverOptions(type) {
    var el = input('turn');
    if (!el) return;
    var mode = type === 'spa' ? 'spa' : 'pool';
    if (el.getAttribute('data-calc-mode') === mode) return;
    el.setAttribute('data-calc-mode', mode);
    el.innerHTML = '';
    TURNOVER[mode].options.forEach(function (hours) {
      var opt = document.createElement('option');
      opt.value = hours;
      opt.textContent = hours;
      if (hours === TURNOVER[mode].selected) opt.selected = true;
      el.appendChild(opt);
    });
  }

  function equip() {
    var g = num('eGal');
    var a = num('eArea');
    var type = val('ePtype') || ptype;

    var turn = num('turn') || 8;
    var gpm = g / (turn * 60);
    setOutput('gpm', fmt(gpm) + ' GPM');

    var head = Math.round(num('run') * 0.6 + 10);
    setOutput('head', head + ' ft');

    var pipe = val('pipe');
    var cap = PIPE_CAP[pipe];
    var pipeNote = output('pipeNote');
    if (pipeNote && cap) {
      var over = gpm > cap;
      pipeNote.textContent = over
        ? 'Over what ' + PIPE_LABEL[pipe] + ' pipe carries (~' + cap + ' GPM). Upsize the plumbing or call us.'
        : PIPE_LABEL[pipe] + ' plumbing carries up to ~' + cap + ' GPM — you\'re inside that.';
      pipeNote.setAttribute('data-calc-warn', over ? 'true' : 'false');
    }

    var t = val('ftype');
    if (t === 'cart') {
      setOutput('fsize', fmt(Math.max(100, g / 100)) + ' sq ft');
      setOutput('fsizeLabel', 'Sq ft of cartridge area (1 sq ft per 100 gal, min 100)');
    }
    if (t === 'sand') {
      setOutput('fsize', (Math.ceil(gpm / 15 * 10) / 10).toFixed(1) + ' sq ft');
      setOutput('fsizeLabel', 'Sq ft of sand bed (15 GPM per sq ft)');
    }
    if (t === 'de') {
      setOutput('fsize', fmt(Math.ceil(gpm / 2)) + ' sq ft');
      setOutput('fsizeLabel', 'Sq ft of D.E. grid (2 GPM per sq ft)');
    }

    /* Gas heater: surface area × temperature rise × 12. */
    var btu = a * num('rise') * 12;
    setOutput('btu', fmt(btu) + ' BTU');

    /* Heat pump: surface area × (target water − average air temp) × 12,
       cut by COVER_FACTOR when the pool is covered. The climate select's
       value is that temperature gap in °F. The old formula multiplied
       gallons, which made a deep pool look like it lost more heat than a
       shallow one with the same surface — heat leaves through the surface. */
    var covered = val('cover') === 'yes';
    var hpBtu = a * num('climate') * 12 * (covered ? COVER_FACTOR : 1);
    setOutput('hp', fmt(hpBtu) + ' BTU');

    var sanitizer = val('sanitizer') || 'salt';
    if (sanitizer === 'salt') {
      setOutput('cell', fmt(g * SALT_CELL_FACTOR) + '+ gal');
      setOutput('cellLabel', 'Minimum salt-cell rating (1.5× your pool)');
    }
    if (sanitizer === 'aop') {
      setOutput('cell', fmt(gpm) + '+ GPM');
      setOutput('cellLabel', 'Minimum AOP flow rating (your pump\'s flow)');
    }
    if (sanitizer === 'uv') {
      setOutput('cell', fmt(g) + '+ gal');
      setOutput('cellLabel', 'Minimum UV rating (your full volume)');
    }
    document.querySelectorAll('[data-calc-show-sanitizer]').forEach(function (el) {
      el.hidden = el.getAttribute('data-calc-show-sanitizer') !== sanitizer;
    });

    var saltStart = 3200 / 120 * g / 1000;
    setOutput('saltStart', fmt(saltStart) + ' lbs · ' + Math.ceil(saltStart / 40) + ' bags');

    recs({
      gallons: g,
      area: a,
      gpm: gpm,
      btu: btu,
      hpBtu: hpBtu,
      ptype: type,
      fuel: val('fuel'),
      volt: val('volt'),
      ftype: t,
      sanitizer: sanitizer,
      heating: val('heating') || 'gas'
    });
  }

  /* ---------- 03 recommendations ---------- */
  /* Every product in each part's collection is rendered server-side as a
     hidden card inside that part's tab panel, carrying its variants as
     JSON (snippets/pds-calc-rec-card.liquid): id, rating, fuel, voltage,
     price. Only variants with a rating (custom.calc_rating) are listed, so
     a product nobody has rated can never be recommended.

     Per part: keep cards whose pool type fits, and for each product take
     its cheapest in-stock variant that passes the part's checks and clears
     the requirement. Rank those products: #1 is always the cheapest fit
     ("Best value"); #2–#5 are ordered by a score that weighs price against
     how close the unit is to the ideal size for this pool (FIT_BAND).
     Fewer than two fits adds the "Talk to a pool expert" card. */

  var MAX_RANKED = 5;

  /* Rating ÷ requirement that still counts as a good fit. Above it the
     unit is oversized for this pool and its fit score tapers off. */
  var FIT_BAND = { pump: 1.5, filter: 2.5, heater: 1.6, heatpump: 1.4, chiller: 2, salt: 1.67, aop: 2.5, uv: 2 };
  var PRICE_WEIGHT = 0.55;
  var RANK_BADGE = ['Best value', 'Runner-up', 'Also fits'];

  var SANITIZER_GROUPS = ['salt', 'aop', 'uv'];
  var activeTab = '';
  var tabChosen = false;

  function cardVariants(card) {
    if (!card._calcVariants) {
      var script = card.querySelector('[data-calc-variants]');
      try {
        card._calcVariants = script ? JSON.parse(script.textContent) : [];
      } catch (e) {
        card._calcVariants = [];
      }
    }
    return card._calcVariants;
  }

  /* Blank = no use: tags on the product, so any pool type. An in-ground
     pad's equipment also serves an in-ground spa. */
  function fitsPoolType(card, type) {
    var types = (card.getAttribute('data-calc-pool-types') || '').split(/\s+/).filter(Boolean);
    if (!types.length) return true;
    if (types.indexOf(type) !== -1) return true;
    return type === 'spa' && types.indexOf('ig') !== -1;
  }

  /* Variant voltage is a space-separated list ("115 230"); blank = unknown,
     which doesn't exclude it. */
  function fitsVoltage(variant, volt) {
    if (!variant.volt || !volt) return true;
    return (' ' + variant.volt + ' ').indexOf(' ' + volt + ' ') !== -1;
  }

  function cheaper(a, b) {
    return a.price - b.price || a.rating - b.rating;
  }

  function rank(group, need, ctx, extraCard, extraVariant) {
    var fits = [];
    document.querySelectorAll('[data-calc-rec="' + group + '"]').forEach(function (card) {
      if (!fitsPoolType(card, ctx.ptype)) return;
      if (extraCard && !extraCard(card)) return;
      var best = null;
      cardVariants(card).forEach(function (v) {
        if (!v.available || !(v.rating >= need)) return;
        if (extraVariant && !extraVariant(v)) return;
        if (!best || cheaper(v, best) < 0) best = v;
      });
      if (best) fits.push({ card: card, variant: best });
    });
    if (!fits.length) return fits;

    fits.sort(function (a, b) { return cheaper(a.variant, b.variant); });
    var lowest = fits[0].variant.price || 1;
    var band = FIT_BAND[group] || 2;
    fits.forEach(function (item) {
      var ratio = need > 0 ? item.variant.rating / need : 1;
      item.fit = ratio <= band ? 1 : band / ratio;
      item.score = PRICE_WEIGHT * (lowest / (item.variant.price || lowest)) + (1 - PRICE_WEIGHT) * item.fit;
    });
    var rest = fits.slice(1).sort(function (a, b) { return b.score - a.score || cheaper(a.variant, b.variant); });
    return [fits[0]].concat(rest).slice(0, MAX_RANKED);
  }

  function setIn(root, selector, text) {
    var el = root.querySelector(selector);
    if (el) el.textContent = text;
  }

  function showRanked(group, items, whyFor, expertNeed, ctx) {
    var panel = document.querySelector('[data-calc-panel="' + group + '"]');
    var list = panel && panel.querySelector('[data-calc-list]');
    items.forEach(function (item, i) {
      var card = item.card;
      var v = item.variant;
      var url = card.getAttribute('data-calc-url') || '#';
      card.hidden = false;
      card.setAttribute('data-calc-picked', v.id);
      card.setAttribute('data-calc-rank', i + 1);
      card.classList.toggle('pds-rec--top', i === 0);
      card._calcPrice = v.price;
      setIn(card, '[data-calc-rec-rank]', '#' + (i + 1));
      setIn(card, '[data-calc-rec-badge]', RANK_BADGE[i] || RANK_BADGE[RANK_BADGE.length - 1]);
      setIn(card, '[data-calc-rec-variant]', v.label || '');
      setIn(card, '[data-calc-rec-why]', whyFor(v));
      setIn(card, '[data-calc-rec-price]', v.money);
      var idField = card.querySelector('[data-calc-rec-id]');
      if (idField) idField.value = v.id;
      card.querySelectorAll('[data-calc-rec-link]').forEach(function (link) {
        link.href = url + (url.indexOf('?') === -1 ? '?' : '&') + 'variant=' + v.id;
      });
      if (list) list.appendChild(card);
    });

    var expert = document.querySelector('[data-calc-expert="' + group + '"]');
    if (expert && items.length < 2) {
      expert.hidden = false;
      setIn(expert, '[data-calc-expert-why]', items.length
        ? 'Want a second option for ' + expertNeed + '? We\'ll size one for your ' + POOL_LABEL[ctx.ptype] + ' — free.'
        : 'Nothing we stock is a confirmed fit for ' + expertNeed + ' on your ' + POOL_LABEL[ctx.ptype] + '. Call and we\'ll size it with you.');
      if (list) list.appendChild(expert);
    }

    var tab = document.querySelector('[data-calc-tab="' + group + '"]');
    if (tab) setIn(tab, '[data-calc-tab-count]', items.length ? items.length : 'Call');
    return items;
  }

  function times(rating, g) {
    return (Math.round(rating / g * 10) / 10).toFixed(1) + '×';
  }

  function recs(ctx) {
    var g = ctx.gallons;
    setOutput('recGal', fmt(g));
    setOutput('recGpm', fmt(ctx.gpm));
    setOutput('recArea', fmt(ctx.area));

    document.querySelectorAll('[data-calc-rec], [data-calc-expert]').forEach(function (card) {
      card.hidden = true;
      card.removeAttribute('data-calc-picked');
      card.removeAttribute('data-calc-rank');
    });

    SANITIZER_GROUPS.forEach(function (group) {
      var tab = document.querySelector('[data-calc-tab="' + group + '"]');
      if (tab) tab.hidden = group !== ctx.sanitizer;
    });

    var results = {};
    if (g > 0) {
      var voltOk = function (v) { return fitsVoltage(v, ctx.volt); };

      results.pump = showRanked('pump', rank('pump', ctx.gpm, ctx, null, voltOk),
        function (v) { return 'Rated ' + fmt(v.rating) + ' GPM — clears your ' + fmt(ctx.gpm) + ' GPM'; },
        fmt(ctx.gpm) + ' GPM on ' + ctx.volt + ' V', ctx);

      /* The filter must handle whatever the #1 pump actually pushes. */
      var flow = results.pump.length ? results.pump[0].variant.rating : ctx.gpm;

      results.filter = showRanked('filter', rank('filter', flow, ctx, function (card) {
        var ft = card.getAttribute('data-calc-filter-type');
        return !ft || ft === ctx.ftype;
      }),
        function (v) { return 'Rated ' + fmt(v.rating) + ' GPM — above your pump\'s ' + fmt(flow) + ' GPM'; },
        fmt(flow) + ' GPM', ctx);

      results.heater = showRanked('heater', rank('heater', ctx.btu, ctx, null, function (v) { return v.fuel === ctx.fuel; }),
        function (v) { return 'Rated ' + fmt(v.rating) + ' BTU — covers your ' + fmt(ctx.btu) + ' BTU'; },
        fmt(ctx.btu) + ' BTU on ' + FUEL_LABEL[ctx.fuel], ctx);

      results.heatpump = showRanked('heatpump', rank('heatpump', ctx.hpBtu, ctx, null, voltOk),
        function (v) { return 'Rated ' + fmt(v.rating) + ' BTU — covers your ' + fmt(ctx.hpBtu) + ' BTU'; },
        fmt(ctx.hpBtu) + ' BTU on ' + ctx.volt + ' V', ctx);

      results.chiller = showRanked('chiller', rank('chiller', g, ctx, null, voltOk),
        function (v) { return 'Rated for ' + fmt(v.rating) + ' gal — ' + times(v.rating, g) + ' your pool'; },
        fmt(g) + ' gal', ctx);

      if (ctx.sanitizer === 'salt') {
        results.salt = showRanked('salt', rank('salt', g * SALT_CELL_FACTOR, ctx, null, voltOk),
          function (v) { return 'Rated ' + fmt(v.rating) + ' gal — ' + times(v.rating, g) + ' your pool'; },
          'a ' + fmt(g * SALT_CELL_FACTOR) + ' gal cell', ctx);
      }
      if (ctx.sanitizer === 'aop') {
        results.aop = showRanked('aop', rank('aop', flow, ctx),
          function (v) { return 'Treats ' + fmt(v.rating) + ' GPM — above your ' + fmt(flow) + ' GPM flow'; },
          fmt(flow) + ' GPM', ctx);
      }
      if (ctx.sanitizer === 'uv') {
        results.uv = showRanked('uv', rank('uv', g, ctx),
          function (v) { return 'Rated ' + fmt(v.rating) + ' gal — ' + times(v.rating, g) + ' your pool'; },
          fmt(g) + ' gal', ctx);
      }
    }

    updateTabs(results);
    updateSet(ctx);
  }

  /* ---------- 03 tabs ---------- */
  function visibleTabs() {
    return Array.prototype.filter.call(document.querySelectorAll('[data-calc-tab]'), function (tab) {
      return !tab.hidden;
    });
  }

  function selectTab(group) {
    activeTab = group;
    document.querySelectorAll('[data-calc-tab]').forEach(function (tab) {
      var on = tab.getAttribute('data-calc-tab') === group;
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.tabIndex = on ? 0 : -1;
    });
    document.querySelectorAll('[data-calc-panel]').forEach(function (panel) {
      panel.hidden = panel.getAttribute('data-calc-panel') !== group;
    });
  }

  /* Keep the visitor's tab while it's still on screen; otherwise open the
     first tab that has products, so the row never opens on a phone card. */
  function updateTabs(results) {
    var tabs = visibleTabs();
    if (!tabs.length) return;
    var groups = tabs.map(function (tab) { return tab.getAttribute('data-calc-tab'); });
    if (tabChosen && groups.indexOf(activeTab) !== -1) {
      selectTab(activeTab);
      return;
    }
    var withProducts = groups.filter(function (group) { return results[group] && results[group].length; });
    selectTab(withProducts[0] || groups[0]);
  }

  function initTabs() {
    var tabs = document.querySelectorAll('[data-calc-tab]');
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        tabChosen = true;
        selectTab(tab.getAttribute('data-calc-tab'));
      });
      tab.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        var list = visibleTabs();
        var i = list.indexOf(tab) + (e.key === 'ArrowRight' ? 1 : -1);
        var next = list[(i + list.length) % list.length];
        tabChosen = true;
        selectTab(next.getAttribute('data-calc-tab'));
        next.focus();
        e.preventDefault();
      });
    });
  }

  /* ---------- 03 "add the full set" ---------- */
  /* The set is the #1 pick for what a pad needs: pump, filter, the chosen
     kind of heating and the chosen sanitizer. Chillers are an optional
     extra and stay out of it. */
  function setGroups(ctx) {
    var groups = ['pump', 'filter'];
    if (ctx.heating === 'gas') groups.push('heater');
    if (ctx.heating === 'hp') groups.push('heatpump');
    groups.push(ctx.sanitizer);
    return groups;
  }

  function tabLabel(group) {
    var tab = document.querySelector('[data-calc-tab="' + group + '"]');
    return tab && tab.firstChild ? tab.firstChild.textContent.trim() : group;
  }

  var setCards = [];

  function updateSet(ctx) {
    var box = document.querySelector('[data-calc-set]');
    if (!box) return;
    setCards = [];
    setGroups(ctx).forEach(function (group) {
      var card = document.querySelector('[data-calc-rec="' + group + '"][data-calc-rank="1"]');
      if (card && !card.hidden) setCards.push(card);
    });
    var total = setCards.reduce(function (sum, card) { return sum + (card._calcPrice || 0); }, 0);
    setOutput('setParts', setCards.map(function (card) { return tabLabel(card.getAttribute('data-calc-rec')); }).join(' · '));
    setOutput('setTotal', money(total));
    setOutput('setMsg', '');
    /* A "set" of one is just the card's own Add to cart button. */
    box.hidden = setCards.length < 2;
  }

  function addSet(button) {
    var items = setCards.map(function (card) {
      return { id: Number(card.getAttribute('data-calc-picked')), quantity: 1 };
    });
    if (!items.length) return;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    button.disabled = true;
    setOutput('setMsg', '');
    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) throw new Error(data.description || data.message || 'Couldn\'t add the set to your cart');
          return data;
        });
      })
      .then(function () {
        window.location.href = button.getAttribute('data-calc-cart-url') || root + 'cart';
      })
      .catch(function (err) {
        button.disabled = false;
        setOutput('setMsg', err.message + ' — call us and we\'ll put the order together.');
      });
  }

  /* ---------- 05 help form ---------- */
  /* "What are you sizing?" is a multi-select (checkboxes), but Shopify's
     contact form only accepts flat key/value fields, so the checked
     labels are joined into one hidden contact[sizing] field on every
     change. Kept independent of quick()/volume()/chem()/equip() — this
     runs even on pages where none of those exist. */
  function initHelpForm() {
    var sizingChips = document.querySelectorAll('[data-calc-sizing-chip]');
    var sizingValue = document.querySelector('[data-calc-sizing-value]');
    if (sizingChips.length && sizingValue) {
      var syncSizing = function () {
        var checked = [];
        sizingChips.forEach(function (chip) {
          if (chip.checked) checked.push(chip.value);
        });
        sizingValue.value = checked.join(', ');
      };
      sizingChips.forEach(function (chip) { chip.addEventListener('change', syncSizing); });
      syncSizing();
    }

    /* Snapshots the calculator's current output text into the form's hidden
       contact[...] fields right before it submits, so Ben gets the live
       numbers regardless of which calculators the visitor actually touched. */
    var summary = document.querySelector('[data-calc-hidden-summary]');
    var helpForm = summary && summary.closest('form');
    if (helpForm) {
      helpForm.addEventListener('submit', function () {
        helpForm.querySelectorAll('[data-calc-hidden-summary]').forEach(function (hidden) {
          var out = output(hidden.getAttribute('data-calc-hidden-summary'));
          hidden.value = out ? out.textContent : '';
        });
      });
    }
  }

  /* ---------- init ---------- */

  if (input('cGal')) {
    onInput(['cGal', 'fcNow', 'fcTgt', 'shTgt', 'taNow', 'taTgt', 'cyaNow', 'cyaTgt', 'saltNow', 'saltTgt', 'chNow', 'chTgt'], chem);
    chem();
  }

  if (input('eGal')) {
    onInput(['eGal', 'eArea', 'turn', 'run', 'pipe', 'volt', 'ftype', 'rise', 'fuel', 'climate', 'cover', 'sanitizer', 'heating'], equip);
    var ptypeSelect = input('ePtype');
    if (ptypeSelect) {
      ptypeSelect.addEventListener('change', function () { selectPoolType(ptypeSelect.value); });
    }
    var setButton = document.querySelector('[data-calc-action="add-set"]');
    if (setButton) {
      setButton.addEventListener('click', function () { addSet(setButton); });
    }
    initTabs();
    setTurnoverOptions(ptype);
    equip();
  }

  if (input('vL')) {
    document.querySelectorAll('[data-calc-chip="ptype"]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        selectPoolType(chip.getAttribute('data-calc-value'));
      });
    });

    document.querySelectorAll('[data-calc-shape]').forEach(function (tile) {
      tile.addEventListener('click', function () {
        setShape(tile.getAttribute('data-calc-shape'));
        volume();
      });
    });

    onInput(DIMENSION_FIELDS, volume);

    /* A returning visitor's pool is restored instead of the defaults. */
    var saved = loadState();
    if (saved) {
      if (saved.shape) setShape(saved.shape);
      if (saved.ptype) setChip(saved.ptype);
      DIMENSION_FIELDS.forEach(function (name) {
        if (typeof saved[name] === 'number') setInput(name, saved[name]);
      });
    }

    showFields();
    volume();
  }

  /* The hero widget only takes over the shared pool once the visitor
     actually types in it, so a restored pool isn't overwritten on load. */
  if (input('qL')) {
    onInput(['qL', 'qW', 'qD'], quickToVolume);
    quick();
  }

  var useGalBtn = document.querySelector('[data-calc-action="use-gallons"]');
  if (useGalBtn) {
    useGalBtn.addEventListener('click', function () {
      propagateGallons();
      var chemSection = document.getElementById('chem');
      if (chemSection) chemSection.scrollIntoView({ behavior: 'smooth' });
    });
  }

  initHelpForm();
})();
